import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff, FiRefreshCw, FiLock, FiUser } from "react-icons/fi";
import { login, logout, selectAuth } from "../../store/authSlice";
import { getToken } from "../../lib/api";
import RenderToast from "../../appcomponents/RenderToast";
import ForgotPasswordModal from "../../Modal/ForgotPassword";
import styles from "./Login.module.css";

// Generate a simple math captcha.
const makeCaptcha = () => {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  const ops = ["+", "-", "*"];
  const op = ops[Math.floor(Math.random() * ops.length)];
  let answer;
  let first = a;
  let second = b;
  if (op === "+") answer = a + b;
  else if (op === "-") {
    // keep result non-negative
    first = Math.max(a, b);
    second = Math.min(a, b);
    answer = first - second;
  } else answer = a * b;
  return { question: `${first} ${op} ${second}`, answer: String(answer) };
};

export default function Login({ mode = "admin" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, token, user } = useSelector(selectAuth);

  const [form, setForm] = useState({ username: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [captcha, setCaptcha] = useState(makeCaptcha);
  const [captchaInput, setCaptchaInput] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const refreshCaptcha = useCallback(() => {
    setCaptcha(makeCaptcha());
    setCaptchaInput("");
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const captchaValid = useMemo(
    () => captchaInput.trim() === captcha.answer,
    [captchaInput, captcha]
  );

  const isAdminUser = Boolean(user?.is_super);
  const isLoggedIn = Boolean(token || getToken());
  const loginLabel = mode === "admin" ? "Admin Login" : "User Login";
  const canLogin = mode === "admin" ? isAdminUser : !isAdminUser;
  const dashboardPath = mode === "admin" ? "/admin/dashboard" : "/user/dashboard";

  if (isLoggedIn && user && canLogin) {
    return <Navigate to={dashboardPath} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!form.username.trim()) {
      RenderToast({ type: "error", message: "Username is required." });
      return;
    }
    if (!form.password) {
      RenderToast({ type: "error", message: "Password is required." });
      return;
    }
    if (!captchaValid) {
      RenderToast({ type: "error", message: "Incorrect captcha answer." });
      refreshCaptcha();
      return;
    }

    const result = await dispatch(login(form));
    if (login.fulfilled.match(result)) {
      const roleAllowed = mode === "admin" ? Boolean(result.payload?.is_super) : !Boolean(result.payload?.is_super);

      if (!roleAllowed) {
        dispatch(logout());
        RenderToast({
          type: "error",
          message: "Incorrect credentials.",
        });
        refreshCaptcha();
        return;
      }

      RenderToast({ type: "success", message: "Welcome back!" });
      navigate(dashboardPath, { replace: true });
    } else {
      RenderToast({ type: "error", message: result.payload || "Login failed." });
      refreshCaptcha();
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.brandPanel}>
        <span className={styles.brandPattern} aria-hidden="true" />
        <div className={styles.brandContent}>
          <img
            src="/img/logo/mit-logo-w.webp"
            alt="MIT4Doctors"
            className={styles.brandLogo}
          />
          <h2 className={styles.brandTitle}>Welcome back</h2>
          <p className={styles.brandText}>
            Sign in to manage blogs, users, and permissions from one place.
          </p>
        </div>
      </div>

      <div className={styles.formPanel}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1 className={styles.title}>{loginLabel}</h1>
            <p className={styles.subtitle}>
              {mode === "admin"
                ? "Sign in to manage admin tools and permissions."
                : "Sign in with a user account to access your allowed pages."}
            </p>
            <div className={styles.modeTabs}>
            <button
              type="button"
              className={mode === "admin" ? styles.modeTabActive : styles.modeTab}
              onClick={() => navigate("/auth/admin/login/", { replace: true })}
            >
              Admin
            </button>
            <button
              type="button"
              className={mode === "user" ? styles.modeTabActive : styles.modeTab}
              onClick={() => navigate("/auth/user/login/", { replace: true })}
            >
              User
            </button>
          </div>
        </div>

        <form className={styles.form} method="post" action="" onSubmitCapture={handleSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.label}>Username or Email</span>
            <div className={styles.inputWrap}>
              <FiUser className={styles.inputIcon} />
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="Enter username or email"
                autoComplete="username"
                className={styles.input}
              />
            </div>
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Password</span>
            <div className={styles.inputWrap}>
              <FiLock className={styles.inputIcon} />
              <input
                type={showPass ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Enter password"
                autoComplete="current-password"
                className={styles.input}
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowPass((s) => !s)}
                aria-label={showPass ? "Hide password" : "Show password"}
              >
                {showPass ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            <button
              type="button"
              className={styles.forgotLink}
              onClick={() => setShowForgotPassword(true)}
            >
              Forgot Password?
            </button>
          </label>

          <div className={styles.field}>
            <span className={styles.label}>Security Check</span>
            <div className={styles.captchaRow}>
              <div className={styles.captchaQuestion}>
                {captcha.question} =
              </div>
              <input
                type="text"
                inputMode="numeric"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                placeholder="Answer"
                className={`${styles.input} ${styles.captchaInput}`}
              />
              <button
                type="button"
                className={styles.refreshBtn}
                onClick={refreshCaptcha}
                aria-label="Refresh captcha"
              >
                <FiRefreshCw />
              </button>
            </div>
          </div>

          <button type="submit" className={styles.submit} disabled={loading}>
            {loading ? <span className={styles.spinner} /> : "Sign In"}
          </button>
        </form>
        </div>
      </div>

      <ForgotPasswordModal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
}
